import { FilterOutlined, LoadingOutlined } from '@ant-design/icons'
import { useDebounce } from '@uidotdev/usehooks'
import { Badge, Popover } from 'antd'
import React, { useEffect, useMemo, useState } from 'react'

import { DELAY } from '@/constants'
import useNomenclatureEditorSearch from '@/hooks/search/useNomenclatureEditorSearch'
import useSearch from '@/hooks/search/useSearch'
import useUpdateFilters from '@/hooks/search/useUpdateFilters'
import { filtersService } from '@/services/filters/FiltersService'
import { useFeedStore } from '@/stores/feed/feedStore'
import { useNomenclatureEditorFeedStore } from '@/stores/feed/nomenclatureEditorFeedStore'
import { useSearchStore } from '@/stores/feed/searchStore'
import { useUserInfoStore } from '@/stores/user/userInfoStore'
import type { EntityFilterType, StoreShortWithSupplierType } from '@/types'
import { isNomenclatureEditor } from '@/utils/auth/userRoles'
import { focusProductSearchInput } from '@/utils/search/ui/focusProductSearchInput'

import FilterCheckboxList from '../FilterCheckboxList/FilterCheckboxList'
import cn from './TableColumnFilter.module.scss'

type ColumnFilterItem = EntityFilterType | StoreShortWithSupplierType

type Counted = { count?: number }

const getColumnItemKey = (item: ColumnFilterItem): string =>
  'supplierId' in item ? `${item.supplierId}:${item.code}` : String(item.id)

type TableColumnFilterProps = {
  title: string
  filterType: 'brands' | 'stores'
}

function TableColumnFilter({ title, filterType }: TableColumnFilterProps) {
  const [open, setOpen] = useState(false)
  const [searchValue, setSearchValue] = useState('')
  const [draftSelected, setDraftSelected] = useState<ColumnFilterItem[]>([])
  const [isUpdating, setIsUpdating] = useState(false)
  const [draftTotal, setDraftTotal] = useState<number | null>(null)

  const {
    searchTerm,
    withLeftovers,
    withDiscontinued,
    currentlySearchableCategories,
    currentlySearchableBrands,
    currentlySearchableStores,
    currentlySearchableCharacteristics,
    currentlySearchableParents,
    currentlySearchableNomenclatureGroupId,
    availableFilters,
    setAvailableFilters,
  } = useSearchStore()

  const { selectedOrganisation } = useUserInfoStore()
  const isEditor = useUserInfoStore((state) =>
    isNomenclatureEditor(state.tokenRoles)
  )
  const isOrgSelected = Object.keys(selectedOrganisation).length > 0
  const isBrand = filterType === 'brands'

  const search = useSearch()
  const nomenclatureEditorSearch = useNomenclatureEditorSearch()
  const updateFilters = useUpdateFilters()

  const setProductsLoading = useFeedStore((s) => s.setProductsLoading)
  const feedTotalElements = useFeedStore((s) => s.totalElements)
  const setNomenclatureEditorProductsLoading = useNomenclatureEditorFeedStore(
    (s) => s.setProductsLoading
  )
  const editorTotalElements = useNomenclatureEditorFeedStore(
    (s) => (s.pagination.total as number) ?? 0
  )
  const totalElements = isEditor ? editorTotalElements : feedTotalElements

  const globalApplied = isBrand
    ? currentlySearchableBrands
    : currentlySearchableStores

  const debouncedDraftString = useDebounce(
    JSON.stringify(draftSelected.map(getColumnItemKey).sort()),
    DELAY
  )

  useEffect(() => {
    if (!open) return
    const updateDraftState = async () => {
      setIsUpdating(true)
      const { matchUniformSlottedCharacteristics } = useSearchStore.getState()
      const draftState = {
        searchWithLeftovers: withLeftovers,
        searchWithDiscontinued: withDiscontinued,
        selectedStores:
          filterType === 'stores'
            ? (draftSelected as StoreShortWithSupplierType[])
            : currentlySearchableStores,
        selectedBrands:
          filterType === 'brands'
            ? (draftSelected as EntityFilterType[])
            : currentlySearchableBrands,
        selectedCategories: currentlySearchableCategories,
        selectedParents: currentlySearchableParents,
        selectedCharacteristics: currentlySearchableCharacteristics,
        searchMatchUniformSlotted: matchUniformSlottedCharacteristics,
      }
      const appliedState = {
        withLeftovers,
        withDiscontinued,
        stores: currentlySearchableStores,
        brands: currentlySearchableBrands,
        categories: currentlySearchableCategories,
        parents: currentlySearchableParents,
        characteristics: currentlySearchableCharacteristics,
        matchUniformSlottedCharacteristics,
      }

      const result = await filtersService.handleFacetedFilterUpdate(
        {
          searchTerm,
          draftState,
          appliedState,
          availableFilters,
          totalElements,
        },
        updateFilters
      )
      if (result) {
        setAvailableFilters({
          ...result.availableFilters,
          characteristics: availableFilters?.characteristics,
        })
        setDraftTotal(result.totalElements)
      }
      setIsUpdating(false)
    }
    updateDraftState()
  }, [debouncedDraftString, searchTerm, isEditor])

  const hasChanges = useMemo(() => {
    if (draftSelected.length !== globalApplied.length) return true
    const draftKeys = draftSelected.map(getColumnItemKey).sort().join(',')
    const globalKeys = (globalApplied as ColumnFilterItem[])
      .map(getColumnItemKey)
      .sort()
      .join(',')
    return draftKeys !== globalKeys
  }, [draftSelected, globalApplied])

  const rawItems = useMemo(() => {
    if (!availableFilters) return []
    if (isBrand) return availableFilters.brands || []
    return filtersService.transform.flattenStores(availableFilters) || []
  }, [availableFilters, isBrand])

  const hasAvailableOptions = useMemo(() => {
    return (rawItems as Counted[]).some((item) => (item.count || 0) > 0)
  }, [rawItems])

  const displayItems = useMemo(() => {
    const combinedItemsMap = new Map<string, ColumnFilterItem & Counted>()

    rawItems.forEach((item) => {
      combinedItemsMap.set(getColumnItemKey(item), item)
    })

    draftSelected.forEach((item) => {
      const key = getColumnItemKey(item)
      if (!combinedItemsMap.has(key)) {
        combinedItemsMap.set(key, { ...item, count: 0 })
      }
    })

    const selectedKeys = new Set(draftSelected.map(getColumnItemKey))

    return Array.from(combinedItemsMap.values()).sort((a, b) => {
      const aSel = selectedKeys.has(getColumnItemKey(a))
      const bSel = selectedKeys.has(getColumnItemKey(b))

      if (aSel && !bSel) return -1
      if (!aSel && bSel) return 1

      return (
        (b.count || 0) - (a.count || 0) ||
        (a.description || '').localeCompare(b.description || '')
      )
    })
  }, [rawItems, draftSelected, isBrand])

  const searchedItems = displayItems.filter((item) =>
    (item.description || '')
      .toLowerCase()
      .includes(searchValue.trim().toLowerCase())
  )

  const handleToggle = (itemKey: string) => {
    const item = displayItems.find((x) => getColumnItemKey(x) === itemKey)
    if (!item) return
    setDraftSelected((prev) =>
      prev.some((x) => getColumnItemKey(x) === itemKey)
        ? prev.filter((x) => getColumnItemKey(x) !== itemKey)
        : [...prev, item]
    )
  }

  useEffect(() => {
    if (!open) return
    const close = () => setOpen(false)
    window.addEventListener('scroll', close, { passive: true })
    return () => window.removeEventListener('scroll', close)
  }, [open])

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen)

    if (newOpen) {
      // Синхронизируем локальный выбор с глобальным при открытии
      setDraftSelected(globalApplied)
      setDraftTotal(totalElements)
    } else {
      setSearchValue('')
      focusProductSearchInput(350)
    }
  }

  const handleApply = () => {
    filtersService.applyFilters({
      isNomenclatureEditor: isEditor,
      searchTerm,
      searchWithLeftovers: withLeftovers,
      searchWithDiscontinued: withDiscontinued,
      selectedParents: currentlySearchableParents,
      selectedCategories: currentlySearchableCategories,
      selectedBrands: isBrand
        ? (draftSelected as EntityFilterType[])
        : currentlySearchableBrands,
      selectedStores: !isBrand
        ? (draftSelected as StoreShortWithSupplierType[])
        : currentlySearchableStores,
      selectedCharacteristics: currentlySearchableCharacteristics,
      currentlySearchableNomenclatureGroupId,
      setters: {
        setProductsLoading,
        setNomenclatureEditorProductsLoading,
        setCurrentlySearchableBrands:
          useSearchStore.getState().setCurrentlySearchableBrands,
        setCurrentlySearchableCategories:
          useSearchStore.getState().setCurrentlySearchableCategories,
        setCurrentlySearchableStores:
          useSearchStore.getState().setCurrentlySearchableStores,
        setCurrentlySearchableCharacteristics:
          useSearchStore.getState().setCurrentlySearchableCharacteristics,
        setCurrentlySearchableParents:
          useSearchStore.getState().setCurrentlySearchableParents,
        setWithLeftovers: useSearchStore.getState().setWithLeftovers,
        setWithDiscontinued: useSearchStore.getState().setWithDiscontinued,
      },
      searchHook: search,
      nomenclatureEditorSearchHook: nomenclatureEditorSearch,
    })
    setOpen(false)
    focusProductSearchInput(350)
  }

  const content = (
    <div
      className={`${cn.popoverContent} ${isOrgSelected ? cn.organisationSelected : ''}`}
      onClick={(e) => e.stopPropagation()}
    >
      <span className={cn.popoverTitle}>{title}</span>
      <FilterCheckboxList
        items={searchedItems.map((item) => {
          const itemKey = getColumnItemKey(item)
          return {
            key: itemKey,
            label: item.description,
            count: item.count || 0,
            checked: draftSelected.some(
              (sel) => getColumnItemKey(sel) === itemKey
            ),
          }
        })}
        searchValue={searchValue}
        searchPlaceholder={isBrand ? 'Название бренда' : 'Название склада'}
        onSearchChange={setSearchValue}
        onToggle={handleToggle}
      />
      <div className={cn.footer}>
        <button
          className={cn.resetBtn}
          onClick={() => setDraftSelected([])}
          disabled={isUpdating || draftSelected.length === 0}
        >
          Сбросить
        </button>
        <button
          className={`${cn.applyBtn} ${isOrgSelected ? cn.organisationSelected : ''}`}
          onClick={handleApply}
          disabled={isUpdating || !hasChanges}
        >
          {isUpdating ? <LoadingOutlined /> : `Показать (${draftTotal ?? 0})`}
        </button>
      </div>
    </div>
  )

  const isApplied = globalApplied.length > 0
  const isInteractive = hasAvailableOptions || isApplied

  if (!isInteractive) {
    return <div className={cn.plainHeader}>{title}</div>
  }

  const headerClass = [
    cn.headerCell,
    isApplied ? cn.applied : '',
    open ? cn.opened : '',
    isOrgSelected ? cn.organisationSelected : '',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <Popover
      content={content}
      trigger="click"
      open={open}
      onOpenChange={handleOpenChange}
      placement="bottom"
      align={{ overflow: { adjustX: false, adjustY: false } }}
      destroyTooltipOnHide={false}
      rootClassName={cn.tableColumnPopover}
    >
      <div className={headerClass} onClick={(e) => e.stopPropagation()}>
        <span className={cn.titleText}>{title}</span>
        <Badge
          count={globalApplied.length}
          size="small"
          offset={[2, -2]}
          classNames={{
            indicator: `${cn.countBadge} ${isOrgSelected ? cn.countBadgeOrg : ''}`,
          }}
        >
          <FilterOutlined className={cn.filterIcon} />
        </Badge>
      </div>
    </Popover>
  )
}

export default React.memo(TableColumnFilter)
